import * as React from 'react'
import axios, { AxiosResponse } from 'axios'
import { merge } from 'lodash'
import { ComplexListing, ComplexListingMinimal, initialComplexListing } from '@bit/jimmijoensson.keyhole.interfaces.complex-listing-interface'
import { FetchProps, PaginatedResponseInterface } from '@bit/jimmijoensson.keyhole.interfaces.paginated-response-interface'
import { request } from '../../utils/utils'
import { Filter } from '../../interfaces/Filter'

export interface DarLookUpInterface {
  rentalFound: boolean
}

export interface ComplexListingFilters {
  organizations: Filter[]
}

interface KeyholeListingContextInterface {
  darLookup: (darId: string) => Promise<DarLookUpInterface>
  fetchComplex: (id: string) => Promise<ComplexListing>
  fetchComplexes: (props: FetchProps) => Promise<any>
  filters: ComplexListingFilters | undefined
}

export const KeyholeListingContext = React.createContext<KeyholeListingContextInterface>({
  darLookup: async () => ({rentalFound: false}),
  fetchComplex: async () => initialComplexListing,
  fetchComplexes: async () => {},
  filters: undefined,
})

export const KeyholeListingProvider: React.FunctionComponent<{}> = props => {
  // State variables
  const [filters, setFilters] = React.useState<ComplexListingFilters | undefined>()

  // Callbacks and functions
  const fetchComplexes = async ({ endpoint = '/webflow/complexes', params, cached }: FetchProps) => {
    try {
      const response: PaginatedResponseInterface<ComplexListingMinimal> = await request(
        {
          params: params,
          method: 'get',
          url: process.env.REACT_APP_API_URL + endpoint,
          withCredentials: true,
        },
        cached
      )
      if (response) {
        return response
      }
      return
    } catch (error) {
      throw error
    }
  }

  const fetchComplex = async (id: string) => {
    try {
      let response = await axios({
        method: 'get',
        url: `${process.env.REACT_APP_API_URL}/webflow/complexes/${id}`,
        withCredentials: true,
      })
      const complex: ComplexListing = merge({}, initialComplexListing, response.data)
      return complex
    } catch (e) {
      throw e
    }
  }

  const fetchFilters = async () => {
    try {
      let response: AxiosResponse<ComplexListingFilters> = await axios({
        method: 'get',
        url: `${process.env.REACT_APP_API_URL}/webflow/filters`,
        withCredentials: true,
      })

      return response.data
    } catch (e) {
      throw e
    }
  }

  const darLookup = async (darId: string) => {
    try {
      let response: AxiosResponse<DarLookUpInterface> = await axios({
        method: 'get',
        params: {
          id: darId
        },
        url: `${process.env.REACT_APP_API_URL}/webflow/dar-lookup`,
        withCredentials: true,
      })
      return response.data
    } catch (e) {
      throw e
    }
  }

  const mounted = React.useCallback(async () => {
    try {
      const filters = await fetchFilters()
      setFilters(filters)
    } catch (error) {
      throw error
    }
  }, [fetchFilters])

  // useEffects
  React.useEffect(() => {
    mounted()
  }, [])

  return (
    <KeyholeListingContext.Provider
      value={{
        darLookup,
        fetchComplex,
        fetchComplexes,
        filters,
      }}
    >
      {props.children}
    </KeyholeListingContext.Provider>
  )
}

export const useKeyholeListingContext = () => React.useContext(KeyholeListingContext)
